import { Component, Input, Output, OnInit, OnDestroy, Inject, ViewChild, EventEmitter, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Response } from '@angular/http';
import { BhtpEditService, ApiPolicyEditResponse, ApiEditPolicy, ApiPolicyGetForEditResponse } from '@bhtp-client-api/edit';
import { BhtpPurchaseService, BhtpPackageService, PackageDetails } from '@bhtp-client-api/purchase';
import { DOCUMENT } from '@angular/platform-browser';
import {
    ApiQuoteRequest, ApiQuoteResponse, ApiResponseContainer, ApiCoverage, ApiCoverageDetail, ApiPolicy, SessionStorage,
    DateInformation, DateParts, ApiTraveler, IApiAddress, ApiAddress, ApiFlight, ApiMessage, ApiPaymentInformation,
    ApiPackage, ApiPackageConfiguration
} from '@bhtp-client-api/base';

import { BhtpRulesService, Rules, FieldRules, TravelerDetailConfig, BhtpStateService, State } from '@bhtp-client-api/eligibility';
import {
    TravelerDetailViewConfig, AddressViewConfig, CoverageType, CoverageViewModel, ModalService, ModalOptions, Mapper, RulesService, FieldConfigs,
    CoverageCartViewModel, CoverageCartPackageViewModel, CoverageCartCoverageViewModel, TypeaheadHelper, RentalInformationViewModel,
    TripDetailsViewModel
} from '@bhtp-client-web/component';

import { EditSectionComponent } from './edit-section.component';
import { EditContainerData } from './view-models/edit-container-data';
import { FlightDataContainer } from './view-models/flights-data.model';
import { EditTripDetailsComponent } from './trip-details';
import { Observable, Observer, Subject, Subscription } from 'rxjs';
import { TravelersData } from './view-models/travelers-data.model';
import {
    Coverage, CoverageContainer, FeatureService, PaymentFormData, PaymentIFrameComponent, Agent, BillingInformation,
    ReceiptData, Traveler, Flight, Address, TripDetails, Country
} from './../shared';

import { EditCoverageListComponent } from './coverages/edit-coverage-list.component';
import { TravelersComponent } from './travelers.component';
import { FlightEditComponent } from './flight-edit.component';
import { EditSessionStorage } from './edit-session-storage';
import { PolicyDetails } from './view-models/policy-details.model';
import { SubscriptionHelper } from './../shared/helpers';
import { Constants } from '@bhtp-client-web/component';
import { PolicyFlightOverride } from '@bhtp-client-web/component';
import { PageScrollService, PageScrollInstance } from 'ng2-page-scroll';
import { QuoteHelper } from '../shared/helpers/quote.helper';

@Component({
    selector: 'edit-container',
    template: require('./edit-container.component.html'),
    providers: [EditSessionStorage]
})

export class EditContainerComponent implements OnInit, OnDestroy {
    @Input() public policyNumber: string;
    @Input() public agent: Agent;
    @Input() public handlePurchaseError: Subject<ApiMessage[]> = new Subject();

    // Components
    @ViewChild(EditTripDetailsComponent) public tripDetails: EditTripDetailsComponent;
    @ViewChild(EditCoverageListComponent) public coverageListComponent: EditCoverageListComponent;
    @ViewChild(TravelersComponent) public travelers: TravelersComponent;
    @ViewChild(FlightEditComponent) public flightEditComponent: FlightEditComponent;

    @ViewChild('paymentFormComponent')
    public paymentFormComponent: PaymentIFrameComponent;

    // UI Config
    public scrollOffset: number = 75;

    // Events
    public navigateToPolicyDetails: EventEmitter<string> = new EventEmitter<string>(false);
    public editCompleted: EventEmitter<string> = new EventEmitter<string>(false);

    // Hard coded for now
    private merchantProfile = 'BHTP';
    private paymentMethod = 'Compliant';
    private editValidationErrorMessage = 'Policy validation failed. Please try again later, or call us at 844-411-BHTP.';
    private sessionExpiredErrorCode: string = '2107';

    // Edit Container Data
    private editContainer: EditContainerData = new EditContainerData();

    /**
     * Error codes that indicate a refund failed, but a check will be mailed.
     * Navigation should continue for the user, and the operation considered a success.
     */
    private readonly refundByCheckErrorCodes: string[] = ['2827', '2828'];

    // Expand Sections
    private isExpanded: boolean = true;
    private policyDetailsExpanded: boolean = true;
    private coveragesExpanded: boolean = true;
    private travelersExpanded: boolean = true;
    private tripExpanded: boolean = true;
    private flightBuilderExpanded: boolean = true;
    private showPaymentForm: boolean = false;

    // Loading Flags
    private quoting: boolean = false;
    private showFlights: boolean = false;
    private formDataLoaded: boolean = false;

    private coverageCart: CoverageCartViewModel = new CoverageCartViewModel();
    private originalTotalCost: number;
    private originalPremium: number;
    private originalFees: number = 0;
    private quoteErrors: ApiMessage[] = null;

    // Subscriptions
    private handlePurchaseErrorSubscription: Subscription;
    private reQuoteSubscription: Subscription;

    constructor(
        private policyEditService: BhtpEditService,
        private bhtpRulesService: BhtpRulesService,
        private rulesService: RulesService,
        private purchaseService: BhtpPurchaseService,
        private modalService: ModalService,
        private bhtpStateService: BhtpStateService,
        private packageService: BhtpPackageService,
        private sessionStorage: SessionStorage,
        private editSessionStorage: EditSessionStorage,
        private pageScrollService: PageScrollService,
        private featureService: FeatureService,
        private quoteHelper: QuoteHelper,
        private changeDetector: ChangeDetectorRef,
        @Inject(DOCUMENT) private document: any) { }

    public ngOnInit(): void {
        this.handlePurchaseErrorSubscription = this.handlePurchaseError.subscribe((error: ApiMessage[]) => {
            this.paymentFormComponent.reloadIFrame();
        });
    }

    public ngOnDestroy(): void {
        this.handlePurchaseErrorSubscription = SubscriptionHelper.unsubscribeHandler(this.handlePurchaseErrorSubscription);

        if (this.reQuoteSubscription && !this.reQuoteSubscription.closed) {
            this.reQuoteSubscription.unsubscribe();
        }
    }

    public ngAfterViewInit(): void {
        // Component views are initialized
        let dataFromSessionStorage: EditContainerData = this.editSessionStorage.getSession();

        // Ok this means there is data in storage and the policy numbers match so we should load from there
        if (dataFromSessionStorage && dataFromSessionStorage.policyNumber === this.policyNumber) {
            // Get data from session
            this.editContainer = this.editSessionStorage.getSession();

            // Set up the coverage Cart
            this.coverageCart = CoverageCartViewModel.ConvertApiQuoteResponseIntoCoverageCart(this.editContainer.freshQuote, this.editContainer.coverageContainer.optionalCoverages, this.editContainer.policyResponse.policy.premium);
            this.originalTotalCost = this.editContainer.policyResponse.policy.premium;
            this.originalFees = this.editContainer.policyResponse.policy.fees ? this.editContainer.policyResponse.policy.fees : 0;
            this.originalPremium = this.originalTotalCost - this.originalFees;
            this.formDataLoaded = true;
            if (this.editContainer.flightData) {
                this.showFlights = true;
            }
            this.bindValueChangesFromChildSections();
        }
        else {
            this.init();
        }

        if (!this.formDataLoaded) {
            this.modalService.showLoadingModal('Preparing Policy For Edit.');
        }

        // Mark sections as submitted
        this.tripDetails.submitted = true;
        this.coverageListComponent.submitted = true;
        this.travelers.submitted = true;
        this.flightEditComponent.submitted = true;
    }

    public init(): void {
        Observable.merge(this.getStates(), this.initializeAllDataForEdit()).subscribe(
            null,           // Success
            (error) => {    // Error
                this.modalService.hideLoadingModal();
                this.modalService.showMessageModal('Error', 'Policy editing is not available at this time. Please try again later, or call us at 844-411-BHTP.');
            },
            () => {         // Complete
                this.editContainer.policyNumber = this.policyNumber;
                this.mapEditContanierDataToQuoteRequest();
                this.formDataLoaded = true;
                this.getQuote().subscribe(() => {
                    this.bindValueChangesFromChildSections();
                    this.editSessionStorage.setSession(this.editContainer);
                    this.modalService.hideLoadingModal();
                });
            }
        );
    }

    public maxTravelers(): number {
        let max: number;

        if (this.editContainer && this.editContainer.policyResponse &&
            this.editContainer.policyResponse.package &&
            this.editContainer.policyResponse.package.configuration) {
            max = this.editContainer.policyResponse.package.configuration.maximumNumberTravelers;
        }

        // default max travelers to 25 if nothing is returned from the package config.
        return max || 25;
    }

    public getStates(): Observable<boolean> {
        return Observable.create((observer: Observer<boolean>) => {
            // Lets get the list of states for the children
            this.bhtpStateService.getStates().subscribe((statesList) => {
                this.editContainer.states = statesList;
                observer.next(true);
                observer.complete();
            }, (error) => {
                observer.error(error);
                console.log(error);
            });
        });
    }

    public shouldShowTripDetails(): boolean {
        let shouldShow: boolean = true;

        if (this.editContainer.fieldConfigs && this.editContainer.fieldConfigs.policy) {
            shouldShow = (this.editContainer.fieldConfigs.policy.destination.show || this.editContainer.fieldConfigs.policy.tripDates.show);
        }

        return shouldShow;
    }

    public cancelEdit(): void {
        this.editSessionStorage.clearSessionData();
        if (this.policyNumber) {
            this.navigateToPolicyDetails.emit(this.policyNumber);
            this.showPaymentForm = false;
        }
    }

    public backToEdit(): void {
        this.showPaymentForm = false;
        this.changeDetector.detectChanges();
        this.bindValueChangesFromChildSections();
    }

    public submitEdit(): void {
        // Mark sections as submitted
        this.tripDetails.submitted = true;
        this.coverageListComponent.submitted = true;
        this.travelers.submitted = true;
        this.modalService.showLoadingModal('Verifying Edit...');
        // Check if we can submit, note we are sending false to denote that is is not a quick quote.
        this.getDataFromValidForms(false)
            .subscribe(null,
            (e) => {
                console.log('Can not complete edit yet: ' + e);
                this.modalService.hideLoadingModal();
            }, () => {

                // If difference is 0 dollars, run a executePurchaseEditPolicyWithValidation request to submit edit, or report any violation of rules
                if (this.coverageCart.difference === 0) {
                    this.executePurchaseEditPolicyWithValidation()
                        .finally(() => {
                            this.modalService.hideLoadingModal();
                        })
                        .subscribe(null,
                        (error) => {    // Error
                            if (error.messages.length > 0) {
                                this.modalService.showApiMessageModal(this.modalService.getApiMessageModalOptions('Validation Failed', error.messages));
                            }else {
                                this.modalService.showMessageModal('Editing Failed', this.editValidationErrorMessage);
                            }
                        });
                } else {
                    this.executeEditValidation()
                        .finally(() => {
                            this.modalService.hideLoadingModal();
                        })
                        .subscribe(null,
                        (error) => {// Error
                            if (error.messages.length > 0) {
                                this.modalService.showApiMessageModal(this.modalService.getApiMessageModalOptions('Validation Failed', error.messages));
                            }else {
                                this.modalService.showMessageModal('Editing Failed', this.editValidationErrorMessage);
                            }
                        });
                }
            });
    }

    public collapseAllContainers(): void {
        this.setExpandedValue(false);
    }

    public expandAllContainers(): void {
        this.setExpandedValue(true);
    }

    public onPaymentRespond(paymentFormData: PaymentFormData): void {
        this.purchaseQuote(paymentFormData);
    }

    public purchaseQuote(paymentFormData: PaymentFormData): void {
        this.modalService.showLoadingModal('Purchasing...');
        this.editContainer.billingData = paymentFormData.billing;
        this.executePurchaseEditPolicyWithValidation()
            .finally(() => {
                this.modalService.hideLoadingModal();
            })
            .subscribe(null,
            (error) => {    // Error
                this.modalService.hideLoadingModal();

                if (error.messages.length > 0) {
                    this.modalService.showApiMessageModal(this.modalService.getApiMessageModalOptions('Validation Failed', error.messages));
                }else {
                    this.modalService.showMessageModal('Editing Failed', this.editValidationErrorMessage);
                }
            });
    }

    private initializeAllDataForEdit(): Observable<boolean> {
        return Observable.create((observer: Observer<boolean>) => {
            this.policyEditService.getForEdit(this.policyNumber).subscribe(
                (policyResponse: ApiPolicyGetForEditResponse) => {
                    this.editContainer.policyResponse = policyResponse;

                    this.editContainer.flightReminderPolicy = this.convertPolicyIntoFlightOverridePolicy(policyResponse);
                    // Once we have the Policy we can load the rest of the data
                    this.setUpPolicy();
                    this.setupPackageAndFlights();

                    this.setupEditRules()
                        .subscribe(
                        (next) => { },
                        (error) => {
                            observer.error(error);
                        },
                        () => {
                            observer.next(true);
                            observer.complete();
                        });
                },
                (error) => {
                    observer.error(error);
                });
        });
    }

    private setUpPolicy(): void {
        // create view model for the policy details component to display
        this.editContainer.tripDetails = TripDetails.fromPolicyResponse(this.editContainer.policyResponse);
        this.editContainer.policyDetails = PolicyDetails.fromPolicyResponse(this.editContainer.policyResponse);

        // Create view model for the coverages
        this.editContainer.coverageContainer = new CoverageContainer();
        this.editContainer.coverageContainer.rentalInformation = new RentalInformationViewModel();
        this.editContainer.coverageContainer.rentalInformation.rentalCarPickupDate = this.editContainer.policyDetails.rentalCarPickupDate;
        this.editContainer.coverageContainer.rentalInformation.rentalCarReturnDate = this.editContainer.policyDetails.rentalCarReturnDate;

        // Set up the traveler data
        this.editContainer.policyTravelers = TravelersData.createTravelersData(this.editContainer.policyResponse);

        // Create quote request from policy response
        this.editContainer.quoteRequest = ApiPolicyGetForEditResponse.createQuickQuoteRequestIncludeAllOptionalCoveragesInResponse(this.editContainer.policyResponse);
        // Create view models for coverages
        this.editContainer.coverageContainer.includedCoverages = Coverage.createCoverages(this.editContainer.policyResponse.coverages, ['standard', 'extra', 'extra upgrade'], false);
        this.editContainer.coverageContainer.optionalCoverages = Coverage.createCoverages(this.editContainer.policyResponse.coverages, ['upgrade'], false);
    }

    private setupPackageAndFlights(): void {
        let policyPackage: ApiPackage = this.editContainer.policyResponse.package;
        let config: ApiPackageConfiguration = policyPackage.configuration;
        const isAirCare: boolean = this.featureService.isAirCareRatingId(policyPackage.ratingId);
        this.editContainer.showDestinationDetails = !isAirCare;
        this.editContainer.showTripCostDetails = !isAirCare;

        // use correct name as package name will be alias or rating id
        if (this.editContainer.policyDetails) {
            this.editContainer.policyDetails.packageName = policyPackage.name;
        }

        // If configuration didn't come back, we should restrict edit
        if (!config) {
            this.modalService.showMessageModal('Error', 'Configuration was not retrieved correctly.');
        }

        try {
            // If this package wants us to gather flights
            if (Flight.shouldCollectFlights(config.flightRequirementType)) {
                let packageDetails: PackageDetails = <PackageDetails>policyPackage;
                packageDetails.displayName = policyPackage.name;

                this.editContainer.flightData = new FlightDataContainer();
                this.editContainer.flightData.package = packageDetails;
                this.editContainer.flightData.flights = Flight.fromApiEditFlights(this.editContainer.policyResponse.flights);
                this.editContainer.flightData.travelers = TravelersData.getAllTravelers(this.editContainer.policyTravelers);
                this.editContainer.flightData.validateFlights = Flight.shouldValidateFlights(config.flightRequirementType);

                this.editContainer.flightData.acknowledgements = [];
                this.editContainer.policyResponse.flights.forEach(f => {
                    if (f.acknowledgements) {
                        f.acknowledgements.forEach(ack => {
                            this.editContainer.flightData.acknowledgements.push(ack);
                        });
                    }
                });

                this.editContainer.flightData.overrideRequiresFlights = this.editContainer.policyResponse.policy.overrideRequiresFlights;
                this.editContainer.flightData.futureFlightsExpected = this.editContainer.policyResponse.policy.futureFlightsExpected;

                // Because this is edit and if the policy is an AirCare-like policy then we are going open up the validation on flight
                // dates.  This way users can add flights outside their trip dates.  (This is because return and departure dates are based off of flight dates)
                this.editContainer.flightData.validateFlightDates = !isAirCare;

                this.showFlights = true;
            }
        }
        catch (error) {
            this.modalService.showMessageModal('Error', error);
        }
    }

    private setupEditRules(): Observable<boolean> {
        return Observable.create((observer: Observer<boolean>) => {
            this.bhtpRulesService.getPolicyRulesets(this.editContainer.policyResponse.policy.packageRatingId, this.editContainer.policyResponse.policy.policyAddress.stateName, this.editContainer.policyResponse.policy.purchaseDate)
                .subscribe((rulesResponse: Rules) => {
                    try {
                        let fieldConfigs: FieldConfigs = this.rulesService.buildFieldConfigs(rulesResponse.fields, RulesService.EDIT_MODE);

                        fieldConfigs.policy.tripDates.maxTripLength = this.editContainer.policyResponse.package.configuration.maximumTripLength;
                        fieldConfigs.policy.tripDates.minTripLength = this.editContainer.policyResponse.package.configuration.minimumTripLength;
                        fieldConfigs.additionalTraveler.maxAge = fieldConfigs.policyHolder.maxAge = this.editContainer.policyResponse.package.configuration.maximumAge;
                        fieldConfigs.additionalTraveler.minAge = fieldConfigs.policyHolder.minAge = this.editContainer.policyResponse.package.configuration.minimumAge;
                        this.editContainer.fieldConfigs = fieldConfigs;
                        this.editContainer.newTravelerConfig = fieldConfigs.additionalTraveler;
                        this.editContainer.rules = this.bhtpRulesService.mapResponseToRules(this.editContainer.policyResponse.policyRuleSet);

                        this.setupAdditionalTravelerRules();

                        observer.next(true);
                        observer.complete();
                    }
                    catch (error) {
                        observer.error(error);
                    }
                },
                (error) => { observer.error(error); });
        });
    }

    private getQuote(): Observable<boolean> {
        this.quoting = true;
        return Observable.create((observer: Observer<boolean>) => {
            // due to CFAR depending on other coverages we need to quote with selected and
            // without selected coverages so CFAR is rated correctly and coverages display correctly
            this.reQuoteSubscription = Observable.merge(this.quoteAllCoverages(), this.quoteSelectedCoverages()).subscribe(
                null,   // Success
                (error) => {
                    this.quoting = false;
                    this.modalService.hideLoadingModal();
                    
                    if (error.messages instanceof Array) {
                        if (error.messages.some(message => message.code === this.sessionExpiredErrorCode)) {
                            this.handleSessionExpired();
                        } else {
                            const modalOptions: ModalOptions = this.modalService.getApiMessageModalOptions('Error', error.messages);
                            this.modalService.showApiMessageModal(modalOptions);
                            this.quoteErrors = error.messages; 
                        }
                    } else {
                        console.error(error);
                    }
                    observer.error(error);
                },   // Error
                () => { // Complete
                    let unavailableCoveragesMessage = this.quoteHelper.getUnavailableEditCoverageMessage(this.editContainer.coverageContainer,
                        this.editContainer.freshQuote.coverages,
                        this.getTotalSelectedPremium(this.editContainer) !== this.editContainer.policyResponse.policy.premium);


                    // if there was a coverage quote, reconcile the selected coverages with all coverages
                    if (this.editContainer.coverageQuote && this.editContainer.coverageQuote.coverages && this.editContainer.coverageQuote.coverages.optional) {
                        this.editContainer.coverageQuote.coverages.optional.forEach(cqc => {
                            if (this.editContainer.freshQuote.coverages.optional.some(fqc => fqc.ratingId === cqc.ratingId)) {
                                let currentCoverage: ApiCoverageDetail = this.editContainer.freshQuote.coverages.optional.find(fqc => fqc.ratingId === cqc.ratingId);
                                currentCoverage.premium = cqc.premium;
                                currentCoverage.premiumsByDay = cqc.premiumsByDay;
                                currentCoverage.premiumsByLimit = cqc.premiumsByLimit;
                                currentCoverage.premiumsByTraveler = cqc.premiumsByTraveler;
                            }
                        });

                        // coverage quote is done with all currently selected coverages so fees are accurate, whereas
                        // fresh quote doesn't include coverages when calculating premium multiplier fees like FIGA
                        this.editContainer.freshQuote.packageFee = this.editContainer.coverageQuote.packageFee;
                        this.editContainer.freshQuote.totalFees = this.editContainer.coverageQuote.totalFees;
                    }

                    // Set the IncludedCoverageComponent coverages
                    this.editContainer.coverageContainer.includedCoverages = Coverage.createCoverages(this.editContainer.freshQuote.coverages.included, ['standard', 'extra', 'extra upgrade'], false);

                    // Reconcile current coverages with coverages from quote, takes into consideration the non-removeable coverages on the policy
                    let additionalCoverages: ApiCoverageDetail[] = this.editContainer.freshQuote.coverages.optional
                        .filter(c => {
                            return !c.canRemoveCoverage
                                && c.coverageAvailable
                                && c.isSelected
                                && !this.editContainer.coverageContainer.optionalCoverages.some(c1 => c1.ratingId === c.ratingId);
                        });
                    if (additionalCoverages && additionalCoverages.length > 0) {
                        let optionalCoverage: CoverageViewModel[] = CoverageViewModel.createCoverages(additionalCoverages, ['upgrade'], false);
                        this.editContainer.coverageContainer.optionalCoverages.push(...optionalCoverage);
                    }

                    // Now that we have a fresh quote we need to reconcile the original optional coverages selected with those that are not selectd and add them to the optional coverages
                    this.editContainer.coverageContainer.optionalCoverages = Coverage.createCoverages(this.editContainer.freshQuote.coverages.optional, ['upgrade'], false, this.editContainer.coverageContainer.optionalCoverages);
                    // Set up the coverage Cart
                    this.coverageCart = CoverageCartViewModel.ConvertApiQuoteResponseIntoCoverageCart(this.editContainer.freshQuote, this.editContainer.coverageContainer.optionalCoverages, this.editContainer.policyResponse.policy.premium);
                    this.originalTotalCost = this.editContainer.policyResponse.policy.premium;
                    this.originalFees = this.editContainer.policyResponse.policy.fees ? this.editContainer.policyResponse.policy.fees : 0;
                    this.originalPremium = this.originalTotalCost - this.originalFees;

                    if (unavailableCoveragesMessage) {
                        this.modalService.showMessageModal('Edit Updates', unavailableCoveragesMessage);
                    }

                    this.editSessionStorage.setSession(this.editContainer);

                    this.quoting = false;
                    this.quoteErrors = null;
                    observer.next(true);
                    observer.complete();
                });
        });
    }

    private getTotalSelectedPremium(editContainer: EditContainerData): number {
        // coverageQuote is only filled in if there are selected optional coverages
        let quote: ApiQuoteResponse = editContainer.coverageQuote ? editContainer.coverageQuote : editContainer.freshQuote;
        let optionalAmount = this.sumSelectecCoverages(quote.coverages.optional);
        let currentAmount = quote.baseQuoteAmount + optionalAmount + quote.coverageFees;
        return currentAmount;
    }

    private sumSelectecCoverages(coverages: ApiCoverageDetail[]): number {
        let sum = 0;

        if (coverages) {
            coverages.forEach(c => {
                if (c.isSelected) {
                    if (c.coverageLimit && c.premiumsByLimit && c.premiumsByLimit[c.coverageLimit]) {
                        sum += c.premiumsByLimit[c.coverageLimit];
                    } else {
                        sum += c.premium;
                    }
                }
            });
        }

        return sum;
    }

    private quoteSelectedCoverages(): Observable<boolean> {
        return Observable.create((observer: Observer<boolean>) => {
            // if there are selected optional coverages, add those to the request, otherwise
            // don't run a request at all
            if (this.editContainer.coverageContainer && this.editContainer.coverageContainer.optionalCoverages &&
                this.editContainer.coverageContainer.optionalCoverages.some(c => c.isSelected)) {
                let quoteRequest: ApiQuoteRequest = JSON.parse(JSON.stringify(this.editContainer.quoteRequest));
                quoteRequest.coverages = [];
                this.editContainer.coverageContainer.optionalCoverages.forEach(c => {
                    if (c.isSelected) {
                        let selectedCoverage: ApiCoverage = new ApiCoverage();
                        selectedCoverage.ratingId = c.ratingId;
                        selectedCoverage.coverageLimit = c.coverageLimit;
                        quoteRequest.coverages.push(selectedCoverage);
                    }
                });

                this.purchaseService.getQuote(quoteRequest).subscribe(
                    (quoteResponse) => {
                        try {
                            // store the response on a separate quote property
                            this.editContainer.coverageQuote = quoteResponse.response;

                            // All data has been loaded for the child forms
                            observer.next(true);
                            observer.complete();
                        }
                        catch (error) {
                            observer.error(error);
                        }
                    },
                    (error) => { observer.error(error); });
            }
            else {
                // if no quote needs to be run, clear out any previous quotes
                this.editContainer.coverageQuote = null;
                observer.next(true);
                observer.complete();
            }
        });
    }

    private quoteAllCoverages(): Observable<boolean> {
        return Observable.create((observer: Observer<boolean>) => {
            // Let's get a fresh quote and update optional coverages with the ones
            this.purchaseService.getQuote(this.editContainer.quoteRequest).subscribe(
                (quoteResponse) => {
                    try {
                        this.editContainer.freshQuote = quoteResponse.response;

                        // All data has been loaded for the child forms
                        observer.next(true);
                        observer.complete();
                    }
                    catch (error) {
                        observer.error(error);
                    }
                },
                (error) => {
                    observer.error(error);
                });
        });
    }

    private setupAdditionalTravelerRules(): void {
        this.editContainer.canAddTravelers = this.editContainer.policyResponse.policy.canAddTravelers;
        this.editContainer.canRemoveTravelers = this.editContainer.policyResponse.policy.canRemoveTravelers;

        this.editContainer.addressConfig = new AddressViewConfig();
        this.editContainer.addressConfig.address = this.editContainer.fieldConfigs.policyHolder.address;
        this.editContainer.addressConfig.stateOrProvinceEnabled = this.editContainer.fieldConfigs.policy.state.editable;
    }

    private bindValueChangesFromChildSections(): void {
        Observable
            .create((observer) => {
                // Subscribe to valueChanges from sub components this is for quoting
                Observable.merge(this.travelers.valueChanges, this.flightEditComponent.valueChanges).subscribe((v) => {
                    observer.next(true);
                });

                this.tripDetails.valueChanges.subscribe((v: TripDetails) => {
                    // Keep Policy Details in sync with Trip Details
                    this.editContainer.policyDetails.destination = v.destination;
                    if (v.destinationCountry instanceof Country) {
                        this.editContainer.policyDetails.destinationCountry = v.destinationCountry.name;
                    } else if (typeof v.destinationCountry === 'string') {
                        this.editContainer.policyDetails.destinationCountry = v.destinationCountry;
                    }

                    // update dates on container so flights can immediately be checked against trip dates
                    if (v.departureDate) {
                        this.editContainer.tripDetails.departureDate = v.departureDate;
                    }

                    if (v.returnDate) {
                        this.editContainer.tripDetails.returnDate = v.returnDate;
                    }

                    // Update car rental company
                    if (v.carRentalCompany) {
                        this.checkUpdateCarRentalCompany(v.carRentalCompany);
                    }

                    observer.next(true);
                });

                this.coverageListComponent.valueChanges.subscribe((v: CoverageContainer) => {
                    // Update car rental company
                    if (v.rentalInformation && v.rentalInformation.carRentalCompany) {
                        this.checkUpdateCarRentalCompany(v.rentalInformation.carRentalCompany);
                    }

                    observer.next(true);
                });
            })
            .debounceTime(500)
            .subscribe((isQuickQuote) => {
                // Send Quote
                this.getDataFromValidForms(isQuickQuote).subscribe((data: EditContainerData) => {
                    // Run a quote to update edit container
                    this.executeRequote();
                }, (e) => {
                    this.quoting = false;

                    if (this.reQuoteSubscription && !this.reQuoteSubscription.closed) {
                        this.reQuoteSubscription.unsubscribe();
                    }

                    console.log('Can not send quote yet: ' + e);
                });
            });
    }

    private checkUpdateCarRentalCompany(source: any): void {
        let newCarRentalCompany = TypeaheadHelper.extractTypeaheadValue(source);
        let currentCarRentalCompany = TypeaheadHelper.extractTypeaheadValue(this.editContainer.tripDetails.carRentalCompany);

        if (newCarRentalCompany !== currentCarRentalCompany) {
            this.editContainer.tripDetails.carRentalCompany = newCarRentalCompany;
        }
    }

    /*
    * Checks validity and gets all data from child forms
    */
    private getDataFromValidForms(isQuickQuote: boolean): Observable<EditContainerData> {
        return Observable.create((observer: Observer<EditContainerData>) => {
            let canSubmit: boolean = true;
            Observable.merge(this.tripDetails.canContinue(), this.coverageListComponent.canContinue(), this.travelers.canContinue(), this.flightEditComponent.canContinue(isQuickQuote))
                .subscribe((sectionIsValid) => {
                    if (!sectionIsValid) {
                        canSubmit = false;
                        this.quoting = false;
                    }
                }, (e) => {
                    console.log(e);
                }, () => {
                    if (canSubmit) {
                        // Extract values from each section
                        this.tripDetails.writeValues(this.editContainer.tripDetails);
                        this.coverageListComponent.writeValues(this.editContainer.coverageContainer);
                        this.travelers.writeValues(this.editContainer.policyTravelers);
                        this.flightEditComponent.writeValues(this.editContainer.flightData);

                        observer.next(this.editContainer);
                        observer.complete();
                    } else {
                        observer.error('Not all sections are valid');
                    }
                });
        });
    }

    /*
    * Toggles the expansion/contraction of all template elements
    */
    private setExpandedValue(expanded: boolean): void {
        this.policyDetailsExpanded = expanded;
        this.coveragesExpanded = expanded;
        this.travelersExpanded = expanded;
        this.tripExpanded = expanded;
        this.flightBuilderExpanded = expanded;
        this.isExpanded = expanded;
    }

    /*
    * Mapping and executing of quote request
    */
    private executeRequote(): void {
        this.mapEditContanierDataToQuoteRequest();

        if (this.reQuoteSubscription && !this.reQuoteSubscription.closed) {
            this.reQuoteSubscription.unsubscribe();
        }

        // Lets get a fresh quote and update optional coverages and pricing
        this.getQuote().subscribe();
    }

    /*
    * Mapping and executing of an edit check request
    */
    private executeEditValidation(): Observable<boolean> {
        return Observable.create((observer: Observer<boolean>) => {
            this.mapEditContainerDataToEditRequest();
            this.modalService.showLoadingModal('Verifying Edit...');

            let service = this.policyEditService.validateEditPolicyFullResponse(this.editContainer.editRequest);

            service.subscribe((editValidationResponse: ApiResponseContainer<ApiPolicyEditResponse>) => {
                    if (!editValidationResponse && !editValidationResponse.response) {
                        this.modalService.showMessageModal('Error', 'editValidationErrorMessage');
                    } else if (editValidationResponse.response.messages.length > 0) {
                        this.modalService.showApiMessageModal(this.modalService.getApiMessageModalOptions('Validation Failed', editValidationResponse.response.messages));
                    } else {
                        if (editValidationResponse.response.transactionReconciliation.collectPayment) {
                            this.modalService.hideLoadingModal();
                            this.showPaymentForm = true;
                        } else {
                            this.executePurchaseEditPolicyWithValidation()
                            .finally(() => {
                                this.modalService.hideLoadingModal();
                            })
                            .subscribe(null,
                            (error) => {    // Error

                                if (error.messages.length > 0) {
                                    this.modalService.showApiMessageModal(this.modalService.getApiMessageModalOptions('Validation Failed', error.messages));
                                }else {
                                    this.modalService.showMessageModal('Editing Failed', this.editValidationErrorMessage);
                                }
                            });
                        }
                    }

                    observer.next(true);
                    observer.complete();
                }, error => {
                    observer.error(error);
            });

            service.catch((error): any => {
                if (error.messages.length > 0) {
                    this.modalService.showApiMessageModal(this.modalService.getApiMessageModalOptions('Validation Failed', error.messages));
                }else {
                    this.modalService.showMessageModal('Editing Failed', this.editValidationErrorMessage);
                }
            });

        });
    }

    /*
     * Mapping and executing of an edit confirm request
     */
    private executePurchaseEditPolicyWithValidation(): Observable<boolean> {
        return Observable.create((observer: Observer<boolean>) => {
            this.mapEditContainerDataToEditRequest();
            this.editContainer.editRequest.policy.isQuickQuote = false;
            this.policyEditService.purchaseEditPolicyWithValidation(this.editContainer.editRequest)
                .subscribe((editPurchaseResponse: ApiResponseContainer<ApiPolicyEditResponse>) => {
                    // Process
                    if (editPurchaseResponse.messages.length > 0) {
                        // if the error was refund related, it's not an actual navigational error.
                        if (!editPurchaseResponse.messages.every((m) => this.refundByCheckErrorCodes.indexOf(m.code) > -1)) {
                            if (editPurchaseResponse.messages.some(message => message.code === this.sessionExpiredErrorCode)) {
                                this.handleSessionExpired();
                            }
                            else {
                                this.modalService.showApiMessageModal(this.modalService.getApiMessageModalOptions('Validation Failed', editPurchaseResponse.messages));
                            }

                            observer.next(true);
                            observer.complete();

                            return;
                        }
                    }

                    // save to receipt data session storage
                    let receipt: ReceiptData = ReceiptData.createFromEditData(this.editContainer.editResponse, editPurchaseResponse.response, this.editContainer.billingData, this.editContainer.tripDetails, this.editContainer.policyResponse.package);

                    this.sessionStorage.storeValue(`receipt-${receipt.policyNumber}`, JSON.stringify(receipt));
                    this.editSessionStorage.clearSessionData();
                    this.editCompleted.emit(receipt.policyNumber);

                    observer.next(true);
                    observer.complete();
                }, error => {
                    observer.error(error);
                });
        });
    }

    private handleSessionExpired(): void {
        this.modalService.showMessageModal('Session Expired', `To edit your policy, refresh this page. If you're still having issues please contact us at 844-411-BHTP or assist@bhtp.com M-F, 7 a.m. - 7 p.m.CST.`);
        this.editSessionStorage.clearSessionData();
    }

    /*
    * Maps all the edit container details to a quote request
    */
    private mapEditContanierDataToQuoteRequest(): void {
        let newRequest: ApiQuoteRequest = new ApiQuoteRequest();

        // Map Policy Number
        newRequest.policyNumber = this.policyNumber;

        // Convert Policy Details
        newRequest.policy = PolicyDetails.toApiPolicy(this.editContainer.policyDetails);
        newRequest.policy = TripDetails.toApiPolicy(this.editContainer.tripDetails, newRequest.policy);

        // Convert CRCC Dates if present
        if (this.editContainer.coverageContainer && this.editContainer.coverageContainer.rentalInformation) {
            newRequest.policy.rentalCarPickupDate = this.editContainer.coverageContainer.rentalInformation.rentalCarPickupDate;
            newRequest.policy.rentalCarReturnDate = this.editContainer.coverageContainer.rentalInformation.rentalCarReturnDate;
        }

        // Convert the travelers
        newRequest.travelers = Traveler.toApiTravelers(TravelersData.getAllTravelers(this.editContainer.policyTravelers));

        // Convert the flights
        if (this.editContainer.flightData) {

            newRequest.policy.overrideRequiresFlights = this.editContainer.flightData.overrideRequiresFlights;
            newRequest.policy.futureFlightsExpected = this.editContainer.flightData.futureFlightsExpected;

            // If we are not going to override required flights then we need to include them on the request
            if (!this.editContainer.flightData.overrideRequiresFlights) {
                if (this.editContainer.flightData.flights) {
                    newRequest.flights = Flight.toApiEditFlights(this.editContainer.flightData.flights);

                    if (newRequest.flights && newRequest.flights.length > 0) {
                        // run flight eligibility to get an accurate connection count for quotes if the package
                        // requires flight validation
                        newRequest.policy.checkFlightEligibility = this.editContainer.flightData.validateFlights;
                    }
                }
            }

            if (this.editContainer.flightData.acknowledgements) {
                newRequest.policy.acknowledgements = this.editContainer.flightData.acknowledgements;
            }
        }

        this.editContainer.quoteRequest = newRequest;
    }

    /*
     * Maps all the edit container details to a edit check/confirm request
     */
    private mapEditContainerDataToEditRequest(): void {
        let newRequest: ApiPolicyGetForEditResponse = new ApiPolicyGetForEditResponse();

        newRequest.policyNumber = this.policyNumber;

        newRequest.policy = PolicyDetails.toApiEditPolicy(this.editContainer.policyDetails);
        newRequest.policy = <ApiEditPolicy>TripDetailsViewModel.toApiPolicy(this.editContainer.tripDetails, newRequest.policy);

        if (this.editContainer.billingData) {
            newRequest.paymentInformation = BillingInformation.toApiPaymentInformation(this.editContainer.billingData, this.merchantProfile, this.paymentMethod);
        }

        // Convert CRCC dates if present
        if (this.editContainer.coverageContainer && this.editContainer.coverageContainer.rentalInformation) {
            newRequest.policy.rentalCarPickupDate = this.editContainer.coverageContainer.rentalInformation.rentalCarPickupDate;
            newRequest.policy.rentalCarReturnDate = this.editContainer.coverageContainer.rentalInformation.rentalCarReturnDate;
        }

        // Convert coverages
        if (this.editContainer.coverageContainer) {
            newRequest.coverages = Coverage.toApiEditCoverages(this.getAllSelectedAndIncludedCoverages());
        }

        // handle nulling car rental company on crcc not selected
        if (!newRequest.coverages || !newRequest.coverages.some(coverage => coverage.ratingId === 'CRCC')) {
            newRequest.policy.carRentalCompany = null;
        }

        // Convert the travelers
        if (this.editContainer.policyTravelers) {
            newRequest.travelers = Traveler.toApiEditTravelers(TravelersData.getAllTravelers(this.editContainer.policyTravelers));
        }

        // Convert the flights
        if (this.editContainer.flightData) {

            newRequest.policy.overrideRequiresFlights = this.editContainer.flightData.overrideRequiresFlights;
            newRequest.policy.futureFlightsExpected = this.editContainer.flightData.futureFlightsExpected;

            // If we are not going to override required flights then we need to include them on the request
            if (!this.editContainer.flightData.overrideRequiresFlights) {
                if (this.editContainer.flightData.flights) {
                    let includeEmptyFlights: boolean = Flight.areFlightsRequired(this.editContainer.policyResponse.package.configuration.flightRequirementType);
                    newRequest.flights = Flight.toApiEditFlights(this.editContainer.flightData.flights, includeEmptyFlights);
                }
            }

            if (this.editContainer.flightData.acknowledgements) {
                newRequest.policy.acknowledgements = this.editContainer.flightData.acknowledgements;
            }
        }

        this.editContainer.editRequest = newRequest;
    }

    private getAllSelectedAndIncludedCoverages(): Array<CoverageViewModel> {
        let coverages: Array<CoverageViewModel> = new Array<CoverageViewModel>();

        if (this.editContainer.coverageContainer && this.editContainer.coverageContainer.includedCoverages) {
            this.editContainer.coverageContainer.includedCoverages.forEach(includedCoverage => {
                coverages.push(includedCoverage);
            });

            this.editContainer.coverageContainer.optionalCoverages.forEach(optionalCoverage => {
                if (optionalCoverage.isSelected) {
                    coverages.push(optionalCoverage);
                }
            });
        }

        return coverages;
    }

    private get primaryTraveler(): Traveler {
        return this.editContainer.policyTravelers.primaryTraveler;
    }

    private buildBillingForm(): PaymentFormData {
        // create purchase data to be passed to payment form
        let data: PaymentFormData = new PaymentFormData();

        if (this.primaryTraveler.emailGroup) {
            data.emailAddress = this.primaryTraveler.emailGroup.email;
        }

        data.agent = this.agent;
        data.address = this.primaryTraveler.address;
        data.firstName = this.primaryTraveler.firstName;
        data.lastName = this.primaryTraveler.lastName;
        data.primaryTravelerName = data.firstName + ' ' + data.lastName;
        data.billing = this.editContainer.billingData;

        return data;
    }

    private convertPolicyIntoFlightOverridePolicy(policy: ApiPolicyGetForEditResponse): PolicyFlightOverride {
        let convertedPolicy: PolicyFlightOverride = new PolicyFlightOverride();
        convertedPolicy.policyNumber = policy.policyNumber.toString();
        convertedPolicy.futureFlightsExpected = policy.policy.futureFlightsExpected;
        convertedPolicy.overrideRequiresFlights = policy.policy.overrideRequiresFlights;
        convertedPolicy.flightCount = policy.flights ? policy.flights.length : 0;
        convertedPolicy.doesPackageHaveConditionalFlights = policy.package.configuration.flightRequirementType === 'Conditional';
        return convertedPolicy;
    }

    private scrollToFlights(policyNumber: string): void {
        // https://www.npmjs.com/package/ng2-page-scroll
        let pageScrollInstance: PageScrollInstance = PageScrollInstance.simpleInstance(this.document, '#flightBuilder');
        this.pageScrollService.start(pageScrollInstance);
        this.resetFlightSelectionChoice();
    }

    private resetFlightSelectionChoice(): void {
        const tempFlightData: FlightDataContainer = JSON.parse(JSON.stringify(this.editContainer.flightData));
        tempFlightData.overrideRequiresFlights = null;
        tempFlightData.futureFlightsExpected = null;
        tempFlightData.numberOfFlights = tempFlightData.numberOfFlights ? tempFlightData.numberOfFlights : 1;
        this.editContainer.flightData = tempFlightData;
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/policy-edit/edit-container.component.ts